import { Divider, Grid, Loader } from "@aws-amplify/ui-react";
import { API, graphqlOperation, Storage } from "aws-amplify";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import LocalizedStrings from "react-localization";

import { createSparePhoto, deleteSparePhoto } from "graphql/mutations";
import usePermissions from "hooks/usePermission";
import ConfirmModal from "components/utils/ConfirmModal";
import { sparePhotosBySpareID } from "graphql/queries";
import Gallery from "components/utils/Gallery";
import LoaderFullScreen from "components/utils/LoaderFullScreen";
import { dataURItoBlob, readFileAsDataURL } from "components/utils/functions";

const SparePhotos = ({ spareID }) => {
  const [canUpload, canDelete] = usePermissions([
    "spare:upload-photo",
    "spare:delete-photo",
  ]);
  const [loading, setLoading] = useState(false);
  const [sparePhotos, setSparePhotos] = useState([]);
  const [sparePhotosLoading, setSparePhotosLoading] = useState(false);
  const [modalDetails, setModalDetails] = useState({
    visible: false,
    sparePhotoId: null,
  });

  async function onCreate(fileObj) {
    try {
      setLoading(true);

      let fileSrc = "";
      let filePreview = "";

      if (typeof fileObj === "string") {
        fileSrc = `photo_${Date.now()}.jpeg`;
        const blob = dataURItoBlob(fileObj);

        await Storage.put(`spare-photos/${fileSrc}`, blob, {
          contentType: blob.type,
          level: "public",
        });

        filePreview = fileObj;
      } else if (fileObj) {
        fileSrc = `${fileObj.name
          .split(".")
          .slice(0, -1)
          .join(".")}_${Date.now()}.${fileObj.name.split(".").slice(-1)[0]}`;

        await Storage.put(`spare-photos/${fileSrc}`, fileObj, {
          contentType: fileObj.type,
          level: "public",
        });

        filePreview = await readFileAsDataURL(fileObj);
      }

      const newPhoto = (
        await API.graphql({
          query: createSparePhoto.replaceAll("__typename", ""),
          variables: {
            input: {
              spareID,
              photo: fileSrc,
            },
          },
        })
      ).data.createSparePhoto;

      setSparePhotos((prev) => [
        ...(prev ?? []),
        { ...newPhoto, photo: filePreview },
      ]);

      toast.success(strings.successAdded);
    } catch (err) {
      console.log("Error Upload Photo", err);
      toast.error(strings.responses.unexpectedError);
    } finally {
      setLoading(false);
    }
  }

  async function fetchSparePhotos(id) {
    try {
      setSparePhotosLoading(true);
      const sparePhotosData = await API.graphql(
        graphqlOperation(sparePhotosBySpareID, {
          spareID: id,
        })
      );
      const sparePhotosItems = sparePhotosData.data.sparePhotosBySpareID.items;

      const newList = await Promise.all(
        sparePhotosItems?.map(async (sparePhoto) => {
          const src = await Storage.get(`spare-photos/${sparePhoto.photo}`, {
            level: "public",
          });
          return {
            ...sparePhoto,
            photo: src,
          };
        }) || []
      );

      setSparePhotos(newList);
    } catch (err) {
      toast.error(strings.responses.errorFetch);
    } finally {
      setSparePhotosLoading(false);
    }
  }

  async function sparePhotoDelete(id) {
    try {
      setLoading(true);

      const result = await API.graphql(
        graphqlOperation(deleteSparePhoto, { input: { id } })
      );
      const { photo: deletedSrc } = result.data.deleteSparePhoto;
      if (deletedSrc)
        await Storage.remove(`spare-photos/${deletedSrc}`, {
          level: "public",
        });

      setSparePhotos(sparePhotos.filter((item) => item.id !== id));
      setModalDetails({
        visible: false,
        sparePhotoId: null,
      });
      toast.success(strings.successDeleted);
    } catch (err) {
      toast.error(strings.responses[err]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchSparePhotos(spareID);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spareID]);

  return (
    <>
      <div>
        <div className="p-3">
          {sparePhotosLoading ? (
            <Loader />
          ) : (
            <Grid rowGap="15px" columnGap="15px">
              {Object.entries(strings.gallery.tags).map((gal, index) => (
                <>
                  {index > 0 && <Divider className="mt-3 mb-2" />}
                  <Gallery
                    images={
                      sparePhotos
                        .sort(
                          (a, b) =>
                            new Date(a.createdAt) - new Date(b.createdAt)
                        )
                        .map((photoModel) => ({
                          id: photoModel.id,
                          src: photoModel.photo,
                        })) ?? []
                    }
                    onCreate={canUpload && ((photo) => onCreate(photo))}
                    onDelete={canDelete && sparePhotoDelete}
                  />
                </>
              ))}
            </Grid>
          )}
        </div>
        <ConfirmModal
          visible={modalDetails.visible}
          body={strings.formatString(strings.modal.confirmDeleteQuestion)}
          onCancel={() =>
            setModalDetails({
              visible: false,
              sparePhotoId: null,
            })
          }
          onConfirm={() => sparePhotoDelete(modalDetails.sparePhotoId)}
        />
      </div>
      {loading && <LoaderFullScreen />}
    </>
  );
};

const strings = new LocalizedStrings({
  es: {
    title: "Fotos del Repuesto",
    gallery: {
      tags: {
        all: "Todos",
      },
    },
    successAdded: "Foto agregada con éxito",
    successDeleted: "Foto eliminada con éxito",
    responses: {
      errorFetch: "Error al consultar fotos del repuesto",
      unexpectedError: "Ha ocurrido un error inesperado",
    },
    modal: {
      confirmDeleteQuestion: "¿Estás seguro de eliminar esta foto?",
    },
  },
});

export default SparePhotos;
